/**
 * 蜜糖黄 · 阳光黄页 首页
 *
 * 版式：左侧固定导航(炭黑) + 右侧内容区
 * 内容顺序：琥珀 Hero(含搜索) → 轮播图 → 黄页分类墙 → 最新信息卡片 → VIP 引导条
 * 缩略图一律 aspect-square
 */

import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getListings, type ListingItem } from "@/utils/api";
import { useSite } from "@/context/SiteContext";
import { useSiteConfig } from "@/context/SiteConfigContext";
import HoneyNavbar from "@/templates/honey/components/Navbar";
import HoneyFooter from "@/templates/honey/components/Footer";

/** 简单时间差文案 */
function formatTimeAgo(dateStr?: string) {
  if (!dateStr) return "";
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return "刚刚";
  if (minutes < 60) return `${minutes} 分钟前`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} 小时前`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} 天前`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} 个月前`;
  return `${Math.floor(months / 12)} 年前`;
}

function formatViewCount(count?: number) {
  if (!count) return "0";
  if (count >= 10000) return `${(count / 10000).toFixed(1)}w`;
  return String(count);
}

const FEATURES = [
  { icon: "ri-price-tag-3-line", text: "分类清晰 精准直达" },
  { icon: "ri-rocket-line", text: "一键发布 快速上线" },
  { icon: "ri-shield-check-line", text: "诚信为本 安心使用" },
];

export default function HoneyHomePage() {
  const [listings, setListings] = useState<ListingItem[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [bannerIndex, setBannerIndex] = useState(0);
  const [showSearchTip, setShowSearchTip] = useState(false);
  const navigate = useNavigate();
  const { siteName } = useSite();
  const { siteConfig, navCategories } = useSiteConfig();

  const banners = siteConfig?.banner_settings
    ?.filter((b: any) => b.enabled && b.image_url)
    .slice(0, 5) || [];
  const enableVip = siteConfig?.enable_vip ?? true;

  // 挂载即加载：不使用 useSite 守卫，加载中仅显示骨架屏
  const loadListings = useCallback(() => {
    setListLoading(true);
    getListings({ page: 1, page_size: 9 })
      .then((res) => {
        setListings(Array.isArray(res.list) ? res.list : []);
      })
      .catch(() => setListings([]))
      .finally(() => setListLoading(false));
  }, []);

  useEffect(() => {
    loadListings();
  }, [loadListings]);

  // 轮播自动播放
  useEffect(() => {
    if (banners.length < 2) return;
    const timer = setInterval(() => {
      setBannerIndex((prev) => (prev + 1) % banners.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [banners.length]);

  const goPrev = () => setBannerIndex((prev) => (prev - 1 + banners.length) % banners.length);
  const goNext = () => setBannerIndex((prev) => (prev + 1) % banners.length);

  const heroTitle = siteConfig?.hero_title || "信息分类 一站直达";
  const heroSubtitle = siteConfig?.slogan || "分类更清晰，查找更简单，信息都安心。";

  return (
    <div className="min-h-screen bg-[#FFF9EC]">
      <HoneyNavbar />

      {/* 桌面内容区：左侧预留导航宽度 */}
      <div className="lg:pl-60">
        {/* ═══════════ Hero ═══════════ */}
        <section className="relative overflow-hidden bg-gradient-to-br from-[#F0A500] via-[#FFBE30] to-[#F5A800]">
          {/* 装饰图形：黄页票根感 */}
          <div className="pointer-events-none absolute -top-24 -right-24 w-96 h-96 rounded-full border-[22px] border-[#23201A]/5" />
          <div className="pointer-events-none absolute -bottom-32 -left-20 w-80 h-80 rounded-full border-[26px] border-[#23201A]/5" />

          <div className="relative max-w-7xl mx-auto px-4 lg:px-8 py-14 lg:py-20">
            <div className="max-w-2xl">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#23201A] text-[#F0A500] text-[11px] font-bold tracking-wide">
                <i className="ri-store-2-line text-sm" />
                分类信息 · 阳光黄页
              </span>
              <h1 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-black leading-tight text-[#23201A] tracking-tight">
                {heroTitle}
              </h1>
              <p className="mt-3 text-sm sm:text-base text-[#4A3F22] max-w-xl">{heroSubtitle}</p>

              {/* 搜索区 */}
              <div className="mt-7 flex items-stretch bg-[#FFF9EC] rounded-2xl shadow-2xl shadow-[#23201A]/15 p-2 max-w-xl">
                <div className="flex items-center gap-2 flex-1 px-3">
                  <i className="ri-search-line text-[#8B8577]" />
                  <input
                    type="text"
                    placeholder="搜索感兴趣的信息…"
                    className="w-full bg-transparent outline-none text-sm text-[#23201A] placeholder-[#9A917E]"
                    onFocus={() => setShowSearchTip(true)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") setShowSearchTip(true);
                    }}
                  />
                </div>
                <button
                  onClick={() => setShowSearchTip(true)}
                  className="px-6 py-3 rounded-xl bg-[#23201A] text-[#F0A500] text-sm font-bold hover:bg-[#3A3529] transition-colors"
                >
                  搜索
                </button>
              </div>
              {showSearchTip && (
                <p className="mt-2.5 flex items-center gap-1.5 text-xs text-[#5A4C1E] bg-white/60 rounded-lg px-3 py-2 w-fit">
                  <i className="ri-information-line" />
                  搜索功能将在站点后台开通，您可先通过下方分类浏览信息
                </p>
              )}

              {/* 特性小标签 */}
              <div className="mt-8 flex flex-wrap gap-2.5">
                {FEATURES.map((f) => (
                  <span key={f.text} className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#4A3F22] bg-white/55 rounded-full px-3 py-1.5">
                    <i className={`${f.icon} text-[#B87A00]`} />
                    {f.text}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ═══════════ 轮播图（后台启用才显示） ═══════════ */}
        {banners.length > 0 && (
          <section className="max-w-7xl mx-auto px-4 lg:px-8 mt-6">
            <div className="relative overflow-hidden rounded-2xl shadow-md border border-[#EAE1CB]">
              <div
                className="flex transition-transform duration-500"
                style={{ transform: `translateX(-${bannerIndex * 100}%)` }}
              >
                {banners.map((banner: any, idx: number) => (
                  <div key={banner.id || idx} className="w-full shrink-0 relative">
                    {banner.link_url ? (
                      <Link to={banner.link_url} className="block">
                        <img src={banner.image_url} alt={banner.title || "banner"} className="w-full h-44 sm:h-56 lg:h-72 object-cover" />
                      </Link>
                    ) : (
                      <img src={banner.image_url} alt={banner.title || "banner"} className="w-full h-44 sm:h-56 lg:h-72 object-cover" />
                    )}
                  </div>
                ))}
              </div>

              {banners.length > 1 && (
                <>
                  <button
                    onClick={goPrev}
                    aria-label="上一张"
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-[#23201A]/60 text-[#FFF9EC] flex items-center justify-center hover:bg-[#23201A] transition-colors cursor-pointer"
                  >
                    <i className="ri-arrow-left-s-line text-lg" />
                  </button>
                  <button
                    onClick={goNext}
                    aria-label="下一张"
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-[#23201A]/60 text-[#FFF9EC] flex items-center justify-center hover:bg-[#23201A] transition-colors cursor-pointer"
                  >
                    <i className="ri-arrow-right-s-line text-lg" />
                  </button>
                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                    {banners.map((_: any, idx: number) => (
                      <button
                        key={idx}
                        onClick={() => setBannerIndex(idx)}
                        aria-label={`切换到第 ${idx + 1} 张`}
                        className={`h-1.5 rounded-full transition-all cursor-pointer ${
                          idx === bannerIndex ? "w-5 bg-[#F0A500]" : "w-1.5 bg-[#23201A]/30 hover:bg-[#23201A]/50"
                        }`}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          </section>
        )}

        {/* ═══════════ 黄页分类墙 ═══════════ */}
        {navCategories.length > 0 && (
          <section className="max-w-7xl mx-auto px-4 lg:px-8 mt-12">
            <div className="flex items-end justify-between mb-5">
              <div>
                <h2 className="text-xl lg:text-2xl font-black text-[#23201A] flex items-center gap-2">
                  <span className="w-1.5 h-6 bg-[#F0A500] rounded-full inline-block" />
                  分类黄页
                </h2>
                <p className="mt-1 text-xs text-[#8B8577]">按需查找，直达心仪栏目</p>
              </div>
              <Link to="/category" className="inline-flex items-center gap-1 text-[13px] font-bold text-[#B87A00] hover:text-[#23201A] transition-colors">
                查看全部 <i className="ri-arrow-right-s-line" />
              </Link>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {navCategories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className="group flex items-center gap-3 bg-white border border-[#EAE1CB] rounded-xl px-4 py-4 hover:border-[#F0A500] hover:shadow-lg hover:shadow-[#F0A500]/15 hover:-translate-y-0.5 transition-all"
                >
                  <span className="shrink-0 w-11 h-11 rounded-xl bg-[#FFF3D6] text-[#B87A00] flex items-center justify-center group-hover:bg-[#F0A500] group-hover:text-[#23201A] transition-colors">
                    <i className={`${cat.icon || "ri-folder-3-line"} text-xl`} />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-bold text-[#23201A] truncate group-hover:text-[#B87A00] transition-colors">
                      {cat.name}
                    </span>
                    <span className="block text-[11px] text-[#8B8577] mt-0.5">立即浏览</span>
                  </span>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* ═══════════ 最新信息 ═══════════ */}
        <section className="max-w-7xl mx-auto px-4 lg:px-8 mt-12 pb-6">
          <div className="flex items-end justify-between mb-5">
            <div>
              <h2 className="text-xl lg:text-2xl font-black text-[#23201A] flex items-center gap-2">
                <span className="w-1.5 h-6 bg-[#F0A500] rounded-full inline-block" />
                最新发布
              </h2>
              <p className="mt-1 text-xs text-[#8B8577]">新鲜出炉的信息，先到先得</p>
            </div>
            <Link to="/category" className="inline-flex items-center gap-1 text-[13px] font-bold text-[#B87A00] hover:text-[#23201A] transition-colors">
              更多信息 <i className="ri-arrow-right-s-line" />
            </Link>
          </div>

          {listLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="bg-white rounded-xl border border-[#EAE1CB] overflow-hidden">
                  <div className="aspect-square animate-pulse bg-[#F3ECDC]" />
                  <div className="p-4 space-y-2.5">
                    <div className="h-4 w-3/4 rounded bg-[#F3ECDC] animate-pulse" />
                    <div className="h-3 w-full rounded bg-[#F3ECDC] animate-pulse" />
                  </div>
                </div>
              ))}
            </div>
          ) : listings.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {listings.map((item) => {
                const cover = Array.isArray(item.images) && item.images.length > 0 ? item.images[0] : "";
                return (
                  <Link
                    key={item.id}
                    to={`/listing/${item.id}`}
                    className="group bg-white rounded-xl border border-[#EAE1CB] overflow-hidden hover:border-[#F0A500] hover:shadow-lg hover:shadow-[#F0A500]/10 hover:-translate-y-0.5 transition-all"
                  >
                    <div className="aspect-square overflow-hidden bg-[#FFF7E0] relative">
                      {cover ? (
                        <img src={cover} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <i className="ri-image-line text-4xl text-[#E9D9AE]" />
                        </div>
                      )}
                      {item.is_pinned && (
                        <span className="absolute top-2.5 left-2.5 inline-flex items-center gap-1 bg-[#23201A] text-[#F0A500] text-[10px] font-bold px-2 py-1 rounded-md">
                          <i className="ri-pushpin-2-fill" />置顶
                        </span>
                      )}
                    </div>
                    <div className="p-4">
                      <h3 className="text-[15px] font-bold text-[#23201A] line-clamp-1 group-hover:text-[#B87A00] transition-colors">
                        {item.title}
                      </h3>
                      <p className="mt-1.5 text-xs text-[#6E6658] line-clamp-2 leading-5 min-h-[40px]">
                        {typeof item.content === "string" ? item.content : ""}
                      </p>
                      <div className="mt-3 flex items-center justify-between border-t border-dashed border-[#EAE1CB] pt-2.5">
                        <span className="inline-flex items-center gap-1.5 text-[11px] text-[#B87A00] font-semibold bg-[#FFF3D6] rounded-full px-2 py-0.5">
                          <i className="ri-folder-line" />
                          {item.category?.name || "分类"}
                        </span>
                        <span className="inline-flex items-center gap-3 text-[11px] text-[#8B8577]">
                          <span className="inline-flex items-center gap-1">
                            <i className="ri-eye-line" />{formatViewCount(item.view_count)}
                          </span>
                          <span>{formatTimeAgo(item.created_at)}</span>
                        </span>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          ) : (
            <div className="bg-white border border-[#EAE1CB] rounded-xl py-16 text-center">
              <i className="ri-inbox-line text-5xl text-[#E4D9BB]" />
              <p className="mt-4 text-sm text-[#8B8577]">暂无信息，欢迎成为第一位发布者</p>
              <button
                onClick={() => navigate("/publish")}
                className="mt-5 inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-[#F0A500] text-[#23201A] text-sm font-bold hover:bg-[#FFB520] transition-colors cursor-pointer"
              >
                <i className="ri-add-circle-line" />立即发布
              </button>
            </div>
          )}
        </section>

        {/* ═══════════ VIP 引导 ═══════════ */}
        {enableVip && (
          <section className="max-w-7xl mx-auto px-4 lg:px-8 mt-6 pb-14">
            <div className="relative overflow-hidden bg-[#23201A] rounded-2xl px-6 lg:px-10 py-8 flex flex-col sm:flex-row items-center justify-between gap-5">
              <div className="pointer-events-none absolute -top-10 -right-10 w-44 h-44 rounded-full border-[16px] border-[#F0A500]/10" />
              <div className="flex items-center gap-4">
                <span className="w-14 h-14 rounded-2xl bg-[#F0A500] text-[#23201A] flex items-center justify-center shrink-0">
                  <i className="ri-vip-crown-2-line text-3xl" />
                </span>
                <div>
                  <h3 className="text-lg font-black text-[#FFF9EC]">开通 VIP · 信息更加醒目</h3>
                  <p className="mt-1 text-xs text-[#B8B1A0]">专属标识与展示位置，让您的信息被更多用户看见</p>
                </div>
              </div>
              <Link
                to="/vip"
                className="shrink-0 inline-flex items-center gap-2 px-7 py-3 rounded-xl bg-[#F0A500] text-[#23201A] text-sm font-bold hover:bg-[#FFB520] transition-colors"
              >
                查看会员权益 <i className="ri-arrow-right-s-line" />
              </Link>
            </div>
          </section>
        )}

        <HoneyFooter />
      </div>
    </div>
  );
}
